import React from 'react';
import { Notification } from '../../services/notificationService';
import { useNavigate } from 'react-router-dom';
import './NotificationItem.css';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete,
}) => {
  const navigate = useNavigate();

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 60) {
      return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes} minutes ago`;
    } else if (diffInHours < 24) {
      return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
    } else if (diffInDays < 7) {
      return diffInDays === 1 ? 'Yesterday' : `${diffInDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Get icon based on notification type
  const getIcon = (type: string) => {
    switch (type) {
      case 'deal_approved':
        return '✅';
      case 'deal_rejected':
        return '❌';
      case 'comment_reply':
        return '💬';
      case 'upvote_received':
        return '👍';
      case 'downvote_received':
        return '👎';
      case 'points_awarded':
        return '🏆';
      case 'badge_earned':
        return '🥇';
      case 'system_message':
        return '📢';
      default:
        return '🔔';
    }
  };

  // Handle click on notification
  const handleClick = () => {
    if (!notification.isRead) {
      onMarkAsRead(notification._id);
    }

    if (notification.link) {
      navigate(notification.link);
    }
  };

  // Handle delete
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification._id);
  };

  return (
    <div
      className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
      onClick={handleClick}
    >
      <div className="notification-icon">{getIcon(notification.type)}</div>

      <div className="notification-content">
        <div className="notification-header">
          <h4 className="notification-title">{notification.title}</h4>
          <span className="notification-time">{formatDate(notification.createdAt)}</span>
        </div>

        <p className="notification-message">{notification.message}</p>
      </div>

      <button
        className="delete-notification-btn"
        onClick={handleDelete}
        aria-label="Delete notification"
      >
        &times;
      </button>
    </div>
  );
};

export default NotificationItem;
